import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { GlobalI18n } from '@settings/global-i18n';
import { Module } from '@shared/models/module-name';
import { MasterDataService } from '@shared/data-provider/master-data-api/api/masterData.service';
import { convertDateToUTC } from '@shared/functions/dateFunctions';
import { NzModalService } from 'ng-zorro-antd/modal';
import { map } from 'rxjs';
import { MasterDataFunctionsService } from '../../services/master-data.service';
import { Season } from '@shared/data-provider/master-data-api/model/season';

interface ExtendedSeason extends Season {
	seasonName?: string;
	dtRangeStart?: Date;
	dtRangeFinish?: Date;
	rangeStart?: Date;
	rangeFinish?: Date;
}

@Component({
	selector: 'app-seasons',
	templateUrl: './seasons.component.html',
	styleUrls: ['./seasons.component.less'],
})
export class SeasonsComponent implements OnInit {
	constructor(
		private apiMasterData: MasterDataService,
		private modal: NzModalService,
		private fb: UntypedFormBuilder,
		private globalI18n: GlobalI18n,
		private functionsService: MasterDataFunctionsService
	) {}

	private _list: ExtendedSeason[] = [];
	selectedItem: ExtendedSeason;
	form: FormGroup;

	listOfDisplayTypes = [
		{ value: 0, label: 'Winter' },
		{ value: 1, label: 'Summer' },
	];

	settingValue = {
		loadingList: false,
		loadingModal: false,
		saving: false,
		searchName: '',
		isVisibleModal: false,
		loadedPercent: null,
		pageIndex: 1,
	};

	listOfColumn = [];

	ngOnInit() {
		this.loadList();
		this.listOfColumn = [
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnType'),
				compare: (a: ExtendedSeason, b: ExtendedSeason) => a.seasonName.localeCompare(b.seasonName),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnStart'),
				compare: (a: ExtendedSeason, b: ExtendedSeason) =>
					Date.parse(a.rangeStart.toDateString()) - Date.parse(b.rangeStart.toDateString()),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnFinish'),
				compare: (a: ExtendedSeason, b: ExtendedSeason) =>
					Date.parse(a.rangeFinish.toDateString()) - Date.parse(b.rangeFinish.toDateString()),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnLastUpdate'),
				compare: (a: ExtendedSeason, b: ExtendedSeason) => Date.parse(a.lastupdate) - Date.parse(b.lastupdate),
				priority: false,
			},
		];
	}

	loadList() {
		this.settingValue.loadingList = true;
		this.apiMasterData.selectRecords('seasons').subscribe((data: Season[]) => {
			this._list = data.map(item => {
				return {
					...item,
					seasonName: item.type === 0 ? 'Winter' : 'Summer',
					dtRangeStart: new Date(item.dtRange[0]),
					dtRangeFinish: new Date(item.dtRange[1]),
					rangeStart: new Date(item.range[0]),
					rangeFinish: new Date(item.range[1]),
				};
			});
			this.settingValue.loadingList = false;
		});
	}

	get listOfDisplay(): ExtendedSeason[] {
		// При установленных фильтрах необходимо сбросить пагинацию на 1 страницу
		if (this.settingValue.searchName.length !== 0) {
			this.onPageIndexChanged(1);
		}
		return this._list.filter(item => {
			return item.seasonName.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1;
		});
	}

	openItem(id: number) {
		this.settingValue.loadingList = true;
		return this.apiMasterData
			.selectRecord('seasons', id.toString())
			.pipe(
				map((item: Season) => {
					return {
						...item,
						seasonName: item.type === 0 ? 'Winter' : 'Summer',
						dtRangeStart: new Date(item.dtRange[0]),
						dtRangeFinish: new Date(item.dtRange[1]),
						rangeStart: new Date(item.range[0]),
						rangeFinish: new Date(item.range[1]),
					};
				})
			)
			.subscribe((data: ExtendedSeason) => {
				this.selectedItem = data;
				this.form = this.createForm(data);
				this.showModal();
				this.settingValue.loadingList = false;
			});
	}

	addNew() {
		this.selectedItem = {};
		this.form = this.createForm();
		this.showModal();
	}

	createForm(data?: ExtendedSeason) {
		return this.fb.group({
			type: [data ? data.type : '', [Validators.required]],
			dtRangeStart: [data ? convertDateToUTC(new Date(data.dtRangeStart)) : new Date(), [Validators.required]],
			dtRangeFinish: [
				data ? convertDateToUTC(new Date(data.dtRangeFinish)) : new Date('2099-12-31 23:59'),
				[Validators.required],
			],
			rangeStart: [data ? convertDateToUTC(new Date(data.rangeStart)) : new Date(), [Validators.required]],
			rangeFinish: [
				data ? convertDateToUTC(new Date(data.rangeFinish)) : new Date('2099-12-31 23:59'),
				[Validators.required],
			],
		});
	}

	add() {
		this.form.markAllAsTouched();
		if (this.form.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.insertRecord('seasons', null, this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadList();
				},
				error: err => {
					this.displayError(err);
				},
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	save() {
		this.form.markAllAsTouched();
		if (this.form.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.updateRecord('seasons', this.selectedItem.id.toString(), this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadList();
				},
				error: err => this.displayError(err),
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	delete() {
		this.settingValue.saving = true;
		this.form.get('dtRangeFinish').setValue(new Date());
		this.apiMasterData.updateRecord('seasons', this.selectedItem.id.toString(), this.createFromForm()).subscribe({
			next: () => {
				this.closeModal();
				this.loadList();
			},
			error: err => this.displayError(err),
			complete: () => {
				this.settingValue.saving = false;
			},
		});
	}

	duplicate() {
		this.selectedItem.id = undefined;
		this.selectedItem.lastupdate = undefined;
	}

	showDeleteConfirm(): void {
		this.modal.confirm({
			nzTitle: this.globalI18n.getMessage(Module.MasterData, 'deleteItem'),
			nzOkText: this.globalI18n.getMessage(Module.MasterData, 'yes'),
			nzOkType: 'primary',
			nzOkDanger: true,
			nzOnOk: () => this.delete(),
			nzCancelText: this.globalI18n.getMessage(Module.MasterData, 'no'),
			nzOnCancel: () => console.log('Cancel'),
		});
	}

	createFromForm(): Season {
		return {
			...this.selectedItem,
			...this.form.value,
			name: [this.form.get('nameEn').value, this.form.get('nameRu').value],
			dtRange: [this.form.get('dtRangeStart').value, this.form.get('dtRangeFinish').value],
			range: [this.form.get('rangeStart').value, this.form.get('rangeFinish').value],
		};
	}

	downloadCsvFile() {
		this.functionsService.downloadCsvFile('seasons', this.settingValue.loadingList);
	}

	downloadXmlFile() {
		this.functionsService.downloadXmlFile('seasons', this.settingValue.loadingList);
	}

	onPageIndexChanged(index: number) {
		this.settingValue.pageIndex = index;
	}

	showModal(): void {
		this.settingValue.isVisibleModal = true;
	}

	closeModal(): void {
		this.settingValue.isVisibleModal = false;
	}

	displayError(err): void {
		this.modal.create({
			nzTitle: 'Error',
			nzContent: err.message,
			nzClosable: false,
		});
	}
}
